import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import I18n from 'I18n';
import { useRef } from 'react';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useSignupData } from '../signup-data/useSignupData';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { ButtonsGridStyled, ButtonsGridButtonHolder, ButtonsGridButton } from '../styles/ButtonsGrid.styles';
import { getTracker } from '../tracking/getTracker';
import { EventName } from '../tracking/EventName';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';

const defaultGetButtonText = ({
  _dataKey,
  key
}) => {
  return I18n.text(`views.${_dataKey}.options.${key}`);
};

export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  shouldDisableAutofocus = false,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false
}) => {
  const gridRef = useRef(null);
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const actualColumnCount = columnCount || preferredColumnCount;
  const {
    currentStep
  } = useCurrentStep();
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();

  const onSelectedChange = value => {
    onSelect(value);
    submit();
  };

  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);

  const onClickOption = key => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }

    if (!hasNextButton) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      getTracker().track(EventName.Interaction, {
        action: `highlight ${dataKey} ${key}`
      });
    }
  };

  return /*#__PURE__*/_jsxs(ButtonsGridStyled, {
    currentStep: currentStep,
    isFramedCentredStep: isFramedCentredStep,
    ref: gridRef // @ts-expect-error --option-preferred-column-count is not part of CSSProperties.
    ,
    style: Object.assign({}, actualColumnCount > 0 ? {
      '--option-preferred-column-count': actualColumnCount
    } : null),
    children: [options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder, {
      minHeight: optionHeight,
      children: /*#__PURE__*/_jsx(ButtonsGridButton, {
        block: true,
        className: "ButtonsGrid_Button",
        "data-selenium": `${dataKey}-${index}`,
        "data-selenium-option": key,
        name: 'selectable-button',
        selected: data === key || isSelected(key),
        tabIndex: index,
        textLabel: /*#__PURE__*/_jsx("p", {
          className: "m-all-0",
          children: getButtonText({
            _dataKey: dataKey,
            key,
            index
          })
        }),
        truncatable: false,
        type: type,
        onClick: () => onClickOption(key)
      })
    }, key)), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      onClick: () => onSelectedChange(data)
    })]
  });
};